/**
 * Fields the chat renderer reads from message metadata.blocks.
 * Plan 2 §11.2.2 step 6 stores `{ id, type, resolved, source }`.
 * S41 owns the zod schemas and the resolver; this file only documents
 * the resolved fields the browser needs. Tests stub this object.
 *
 * @typedef {'kpi'|'chart'|'table'|'bar-list'|'cohort'|'breakdown-grid'|'compare'|'callout'|'followups'|'report-header'|'action-plan'|'suggest-analysis'|'run'|'retry'} BlockType
 * @typedef {{ text?: string|number, raw?: string, value?: number, heat?: number, tone?: string }} BlockCell
 */

/** Rows kept visible before the disclosure opens. */
export const COLLAPSE_AFTER = 15;

export const BLOCK_TYPES = [
  'kpi', 'chart', 'table', 'bar-list', 'cohort', 'breakdown-grid', 'compare', 'callout', 'followups',
  // S46b report blocks
  'report-header', 'action-plan',
  // S59c suggestion card
  'suggest-analysis',
  // S60b run panel + retry
  'run', 'retry',
];

const FENCE_RE = /```(kpi|chart|table|bar-list|cohort|breakdown-grid|compare|callout|followups|report-header|action-plan|suggest-analysis|run|retry)(?![:\w-])[^\n]*\r?\n[\s\S]*?```/g;

/**
 * Drop resolved-block fences so the raw JSON is not shown beside the renderer.
 * Leaves legacy ```chart:bar fences alone.
 * @param {string} text
 * @returns {string}
 */
export function stripBlockFences(text) {
  return String(text || '').replace(FENCE_RE, '');
}

/**
 * @param {unknown} block
 * @returns {Record<string, unknown>}
 */
export function resolvedOf(block) {
  const resolved = block && typeof block === 'object' ? block.resolved : null;
  return resolved && typeof resolved === 'object' ? resolved : {};
}

/**
 * @param {unknown} cell
 * @returns {string}
 */
export function cellText(cell) {
  if (cell == null) return '';
  if (typeof cell === 'string' || typeof cell === 'number') return String(cell);
  if (typeof cell === 'object' && cell.text != null) return String(cell.text);
  return '';
}

/**
 * Raw event name when the visible text is a label.
 * @param {unknown} cell
 * @returns {string}
 */
export function cellRaw(cell) {
  if (!cell || typeof cell !== 'object' || cell.raw == null) return '';
  return String(cell.raw);
}

/**
 * @param {unknown} cell
 * @returns {number|null}
 */
export function cellValue(cell) {
  if (typeof cell === 'number' && Number.isFinite(cell)) return cell;
  if (cell && typeof cell === 'object' && cell.value != null && cell.value !== '') {
    const num = Number(cell.value);
    return Number.isFinite(num) ? num : null;
  }
  return null;
}

/**
 * @param {string} title
 * @param {string} label
 * @returns {string}
 */
export function askAboutRow(title, label) {
  const name = label || 'this row';
  return title ? `Tell me more about ${name} in ${title}` : `Tell me more about ${name}`;
}

/**
 * @param {string} series
 * @param {string|number} value
 * @param {string} xLabel
 * @returns {string}
 */
export function askAboutPoint(series, value, xLabel) {
  return `Why is ${series || 'the series'} ${value} on ${xLabel || 'this point'}?`;
}

/**
 * @param {Array<{ key: string, label?: string }>} columns
 * @param {Array<Record<string, unknown>>} rows
 * @returns {boolean[]}
 */
export function rawColumnSlots(columns, rows) {
  return columns.map((column) => rows.some((row) => {
    const raw = cellRaw(row[column.key]);
    return raw !== '' && raw !== cellText(row[column.key]);
  }));
}

/**
 * One short line for a block's data freshness. Old blocks store a string; newer
 * ones store { state, sources: [{ data_as_of }], coverage }.
 * @param {unknown} fresh
 * @returns {string}
 */
export function freshnessText(fresh) {
  if (!fresh) return '';
  if (typeof fresh === 'string') return fresh;
  if (typeof fresh !== 'object') return '';
  const dates = (Array.isArray(fresh.sources) ? fresh.sources : [])
    .map((src) => Date.parse(src && src.data_as_of))
    .filter((t) => Number.isFinite(t));
  const latest = dates.length ? Math.max(...dates) : NaN;
  const when = Number.isFinite(latest)
    ? new Date(latest).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })
    : '';
  const stale = String(fresh.state || '') === 'stale';
  if (stale) return when ? `Data may be out of date (as of ${when})` : 'Data may be out of date';
  return when ? `Data as of ${when}` : '';
}
